const mapList = (items) => {
  if (items.length <= 0) return [];

  const lists = items.map((item) => {
    const { options, label } = item;
    const colorIndex = options.findIndex((option) => option.name === 'Color');

    const colors = Array.from(new Set(options[colorIndex].values));
    const limit =
      item?.limit > 0 && item?.limit <= colors.length
        ? item.limit
        : colors.length;
    const values = colors.slice(0, limit);
    if (values.length <= 0) return [];

    return {
      alias: label,
      name: options[colorIndex].name,
      values: values,
      secondary: true,
    };
  });
  return lists;
};

export const getters = {
  getOptionLists: (state) => {
    return (item) => {
      const data = item ? item : state;
      const limit = parseInt(data.evergreenSwatchCount) ?? false;
      const index = parseInt(data.galleryIndex) ?? false;
      const total =
        index >= 0 ? data.item?.options[index].values.length : false;
      const labels = state.terms.option_labels;
      const secondaryLists =
        state.isQV && item
          ? mapList(item.secondaryItems)
          : state.getSecondaryLists;
      const isAutoBundle = state.isAutoBundle && !state.edit && !state.isQV;
      const lists = Array.from(data.item.options).reduce((options, current) => {
        const isColor = current?.name === 'Color' ?? false;
        const hasSplitLists = !isAutoBundle && limit && limit > 0;
        const values = Array.from(new Set(current.values));
        current.values = values;

        if (isColor && hasSplitLists) {
          const colors = current.values.slice(0, limit);
          const seasonalColors = current.values.slice(limit, total);

          const evergreen = {
            alias: labels.evergreen.name,
            name: current.name,
            values: colors,
            count: {},
          };
          evergreen.count[labels.evergreen.name] = colors.length;
          // combine seasonal and evergreen swatches; keeping track thru obj count
          if (seasonalColors) {
            evergreen.values.push(seasonalColors);
            evergreen.count[labels.seasonal.name] = seasonalColors.length;
          }
          options.push(evergreen);

          const index = options.findIndex((option) => {
            if (option?.name) return option.name.includes('Color');
          });
          options[index].values = options[index].values.flat();

          secondaryLists.forEach((list) => options.push(list));
        } else if (isColor) {
          const containsSeasonal =
            secondaryLists.map((list) => list.alias).includes('Seasonal') ??
            false;

          if (containsSeasonal) {
            const secondaryColors = Array.from(
              new Set(secondaryLists.flatMap((list) => list.values))
            );
            const colors = [...values, ...secondaryColors];

            const data = {
              alias: labels.evergreen.name,
              name: current.name,
              values: colors,
              count: {
                [labels.evergreen.name]: colors.length - secondaryColors.length,
                [labels.seasonal.name]: secondaryColors.length,
              },
            };

            options.push(data);
          } else {
            options.push(current);
          }
        } else {
          options.push(current);
        }
        return options;
      }, []);
      if (lists.length <= 0) console.log('Error in getOptionLists: no options');

      /*
       * Secondary items allow site ops to expose options for other items on any
       * PDP. For example, the Last Call version of a product.
       */
      return lists;
    };
  },
  getSecondaryLists: (state) => {
    let items = [];
    const isBuilding = state.isBuilding ?? false;
    const isEditing = state.isAutoBundle && state.edit;
    if (isBuilding) {
      const currentStep = state.getCurrentStep;
      items = currentStep?.secondaryItems ?? [];
    } else if (isEditing) {
      items = state.edit?.secondaryItems ?? [];
    } else {
      items = state?.secondaryItems ?? [];
    }

    return mapList(items);
  },
  getSelectedValue: (state) => {
    const options = Array.from(state.selected.options);
    return (list) => {
      const selection = options.filter((option) =>
        list.values.includes(option.value)
      );
      const selectionFound = selection.length > 0;
      return selectionFound ? selection[0].value : '';
    };
  },
  shopTheLookEnabled: (state) => {
    return state.shopTheLook && state.shopTheLook.length > 0;
  },
  getSelectedComponents: (state) => {
    const isSteppedBundle = state.isSteppedBundle;

    if (isSteppedBundle) {
      const review = state.steps[state.steps.length - 1];
      const selections = Array.from(review.selected.components);
      return selections;
    }

    const selections = Array.from(state.selected.components);
    return selections;
  },
  getCurrentStep: (state) => {
    const current = state.currentStep || 0;
    const step = state.steps[current] || state.steps[0];
    return step;
  },
  getComponentVariant: (state) => {
    const isSteppedBundle = state.isSteppedBundle;
    if (!isSteppedBundle) return;

    const isReviewStep = state.isReviewStep;
    if (isReviewStep) return;

    const step = state.steps[state.currentStep];
    const { item } = step;
    const { variants } = item;
    const id = parseInt(variants[0].id.split('ProductVariant/')[1]);
    if (window.location.pathname.includes('product')) {
      const params = new URLSearchParams(window.location.search);
      const key = `component${state.currentStep}`;
      const allSelectionsMade = state.stepStatus === '';

      const hasComponent = params.has(key);

      if (hasComponent) {
        const idFromURL = parseInt(params.get(key));
        if (!allSelectionsMade) return idFromURL;

        const variant = variants.filter((variant) => {
          return variant.id.includes(idFromURL);
        });
        return variant[0];
      }

      params.append(key, id);
      const url = `${window.location.pathname}?${params}`;
      window.history.replaceState({}, '', url);
    }
    return id;
  },
  isBuildStep: (state) => {
    const isSteppedBundle = state.isSteppedBundle;
    const buildStep = state.currentStep === 0;

    if (buildStep && isSteppedBundle) return true;

    return false;
  },
  isBuilding: (state) => {
    const isSteppedBundle = state.isSteppedBundle;
    const notReviewing = !state.isReviewStep;
    const building = notReviewing && state.currentStep > 0;

    if (building && isSteppedBundle) return true;

    return false;
  },
  isReviewStep: (state) => {
    const isSteppedBundle = state.isSteppedBundle;
    const reviewStep = state.currentStep === state.steps.length - 1;

    if (reviewStep && isSteppedBundle) return true;

    return false;
  },
  stepStatus: (state) => {
    const isSteppedBundle = state.isSteppedBundle;
    if (!isSteppedBundle) return '';

    const current = state.currentStep || 0;
    const currentStep = state.steps[current];
    const isAvailable = currentStep.selected.available;
    const options = currentStep.selected.options;
    const selections = options.filter((option) => option !== '');
    const totalOptions = currentStep.totalOptions;

    const allSelectionsMade = selections.length >= totalOptions;

    if (allSelectionsMade && isAvailable) return '';

    return 'ProductForm__Cta--Inactive';
  },
  isSteppedBundle: (state) => {
    return state.isBundle && !state.isAutoBundle;
  },
  createRange: () => {
    return (ceiling) =>
      Array.from({ length: ceiling }, (value, index) => (index += 1));
  },
  reviewsEnabled: (state) => {
    return state.item.tags && !state.item.tags.includes('Hide Zero Reviews');
  },
  reviewsProduct: (state) => {
    let tag = state.item.tags.filter((tag) => tag.includes('Review ProductID'));
    return tag.length > 0
      ? tag[0].split(': ')[1]
      : state.item.id.split('/Product/')[1];
  },
  determineSelectedProduct: (state) => {
    const { secondaryItems, item, selected } = state;
    if (secondaryItems.length <= 0) return item;

    const selectedVariant = selected.variant ?? false;
    const galleryIndex = state.galleryIndex;

    if (selectedVariant) {
      const productId = selectedVariant.product.id;
      const secondarySearch = secondaryItems.findIndex(
        (item) => item.id === productId
      );
      return secondarySearch >= 0 ? secondaryItems[secondarySearch] : item;
    }

    const selectedGalleryOption =
      selected.options[galleryIndex]?.value ?? false;

    const secondarySearch = secondaryItems.findIndex((item) => {
      const galleryOptions = item.options[galleryIndex].values;
      return galleryOptions.find((value) => value === selectedGalleryOption);
    });

    return secondarySearch >= 0 ? secondaryItems[secondarySearch] : item;
  },
};
